// Videos page sticky-bg scene positioner.
//
// The scene atom (atoms/_stickyBgScene.twig) renders two sticky layers
// (pattern + video) after .videos-block, both with 0 net flow
// contribution.
//
// This module does two things at runtime (and on resize):
//   1. Sizes the pattern's HEIGHT to exactly one row-pitch of the grid
//      (centre of target card → centre of the card directly below it),
//      so the pattern visually starts at the middle of one thumbnail row
//      and ends at the middle of the next.
//   2. Shifts the pattern's anchor via margin-top so its engagement
//      point (the scroll Y at which sticky kicks in) lines up with the
//      target card's vertical centre.
//
// All flow contributions are kept at 0 by compensating the video's
// margins, so the surrounding page layout is unaffected.

const SELECTOR_PATTERN = '[data-scene-pattern]'
const SELECTOR_VIDEO = '[data-scene-video]'
const SELECTOR_GRID = '.videos-grid'
const SELECTOR_CARD = '.video-card'

const VIDEO_HEIGHT_VH = 100
// Pattern spans from the target card's centre down to the centre of a
// card N rows below. 2 = target row, full row through, end in centre of
// the row after that.
const ROWS_SPANNED = 2
// Pattern height = (centre-to-centre distance) × this. Tune to taste —
// 1.0 lands exactly on the bottom card's centre.
const PATTERN_HEIGHT_FACTOR = 1.0

function countGridColumns(grid) {
    const tpl = window.getComputedStyle(grid).gridTemplateColumns
    return tpl ? tpl.trim().split(/\s+/).length : 1
}

function pickTargetCard(cards) {
    return cards[Math.floor(cards.length / 2)] || null
}

function pickRowBelow(cards, targetIdx, cols) {
    // The card N rows below the target in the same column, with a
    // graceful fallback if there aren't enough cards.
    const span = ROWS_SPANNED * cols
    return cards[targetIdx + span]
        || cards[targetIdx + cols]
        || cards[targetIdx + 1]
        || null
}

function position(pattern, video) {
    const grid = document.querySelector(SELECTOR_GRID)
    if (!grid) return

    const videosBlock = grid.closest('.videos-block')
    if (!videosBlock) return

    const cards = grid.querySelectorAll(SELECTOR_CARD)
    if (cards.length < 2) return

    const cols = countGridColumns(grid)
    const targetIdx = Math.floor(cards.length / 2)
    const target = cards[targetIdx]
    const below = pickRowBelow(cards, targetIdx, cols)
    if (!target || !below) return

    // ---- 1. Compute the pattern's height = N row pitches (centre to centre).
    const targetRect = target.getBoundingClientRect()
    const belowRect = below.getBoundingClientRect()
    const targetCenter = targetRect.top + targetRect.height / 2 + window.scrollY
    const belowCenter = belowRect.top + belowRect.height / 2 + window.scrollY
    const patternHeightPx = Math.max(0, (belowCenter - targetCenter) * PATTERN_HEIGHT_FACTOR)

    // ---- 2. Anchor: videos-block's bottom in the document.
    //
    // CRUCIAL: do NOT read the pattern's own getBoundingClientRect to
    // figure out its "natural" position. The pattern is position:sticky
    // and, once the user has scrolled past the engagement point, its
    // rect is pinned to the viewport (top:0), so the measurement
    // returns the current scrollY instead of the flow Y. That breaks
    // the shift on resize. The videos-block isn't sticky, so its
    // .bottom is always the true flow position of whatever follows it
    // (i.e. the pattern, before any margin-top shift).
    const naturalY = videosBlock.getBoundingClientRect().bottom + window.scrollY
    const shift = targetCenter - naturalY // negative (target is above natural Y)

    // ---- 3. Apply the new height + shift + flow-neutralising margins.
    pattern.style.height = `${patternHeightPx}px`
    pattern.style.marginTop = `${shift}px`
    pattern.style.marginBottom = `${-patternHeightPx}px`
    video.style.marginTop = `${patternHeightPx}px`
    video.style.marginBottom = `calc(-${VIDEO_HEIGHT_VH}vh - ${patternHeightPx}px - ${shift}px)`
}

const videosBgScene = {
    init() {
        const pattern = document.querySelector(SELECTOR_PATTERN)
        const video = document.querySelector(SELECTOR_VIDEO)
        if (!pattern || !video) return

        const run = () => position(pattern, video)
        // Run once now and once more after a tick so cards have measured.
        run()
        window.requestAnimationFrame(run)

        // Debounce resize so a drag-resize doesn't trigger a flurry of
        // half-baked recalcs while the layout is still settling.
        let resizeTimer
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimer)
            resizeTimer = setTimeout(run, 200)
        })
    },
}

export default videosBgScene
